import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';

const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    description: ''
  });

  // Générer un sous-domaine à partir du nom du client (pour les utilisateurs premium)
  const generateSubdomain = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-') // Remplacer les caractères non alphanumériques par des tirets
      .replace(/-+/g, '-') // Remplacer les séquences de tirets par un seul tiret
      .replace(/^-|-$/g, ''); // Supprimer les tirets au début et à la fin
  };

  // Générer un sous-domaine aléatoire (pour les utilisateurs non premium)
  const generateRandomSubdomain = (): string => {
    // Générer une chaîne aléatoire de 8 caractères (lettres minuscules et chiffres)
    const randomString = Math.random().toString(36).substring(2, 10);
    return `client-${randomString}`;
  };

  // Vérifier si l'utilisateur a un plan premium
  useEffect(() => {
    if (user?.planId === 'business') {
      setIsPremium(true);
    } else {
      setIsPremium(false);
    }
  }, [user]);

  // Mettre à jour le sous-domaine automatiquement lorsque le nom change
  useEffect(() => {
    if (formData.name && formData.subdomain === '') {
      if (isPremium) {
        // Pour les utilisateurs premium, générer un sous-domaine basé sur le nom
        setFormData(prev => ({
          ...prev,
          subdomain: generateSubdomain(formData.name)
        }));
      } else {
        // Pour les utilisateurs non premium, générer un sous-domaine aléatoire
        setFormData(prev => ({
          ...prev,
          subdomain: generateRandomSubdomain()
        }));
      }
    }
  }, [formData.name, formData.subdomain, isPremium]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const newClient = await clientService.createClient(formData);
      navigate(`/clients/${newClient.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la création du client');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Créer un nouveau client</h1>
      
      <Card>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Nom du client *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          

          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subdomain">
              Sous-domaine *
            </label>
            <div className="flex items-center">
              <input
                type="text"
                id="subdomain"
                name="subdomain"
                value={formData.subdomain}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
                disabled={!isPremium}
              />
              <span className="ml-2 text-gray-600">.api100.io</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {isPremium 
                ? "Personnalisez votre sous-domaine (lettres minuscules, chiffres et tirets uniquement)"
                : "Le sous-domaine est généré automatiquement à partir du nom du client"}
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              rows={3}
            />
          </div>
          

          
          <div className="flex items-center justify-between">
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              variant="secondary"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Création en cours...' : 'Créer le client'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ClientForm;
